import { differenceInMinutes, format, parseISO } from 'date-fns';

import { type Activity_Types_Enum_Enum } from '@graphql/__generated__/graphql';
import type { ParentTypeOption } from '@modules/_shared/components/feed/types';
import type {
  LeadDetailsCallDialogFormValues,
  LeadDetailsNoteDialogFormValues,
  LeadDetailsTaskDialogFormValues,
  LeadDetailsVisitDialogFormValues,
} from '@modules/_shared/components/feed/utils/activityTypes';
import { isNullOrEmpty } from '@modules/_shared/utils/strings';
import type { LeadDetailsDrawer as LeadDetailsDrawerData } from '@modules/lead-details/type';

type ActivityTargetUserRecord = {
  target_user_id?: string | null;
};

export type ActivityDetails = {
  id: string;
  activity_type: Activity_Types_Enum_Enum;
  subject?: string | null;
  note?: string | null;
  done: boolean;
  success: boolean;
  due_at?: string | null;
  start_date?: string | null;
  end_at?: string | null;
  location?: string | null;
  assigned_to?: string | null;
  created_by?: string | null;
  created_at?: string | null;
  lead_id?: string | null;
  lot_id?: string | null;
  buyer_lead_id?: string | null;
  user_id?: string | null;
  visibility?: 'private' | 'shared' | 'organisation' | null;
  activities_target_users: ActivityTargetUserRecord[];
};

const resolveParentFromActivity = (
  activity: ActivityDetails,
  fallbackLeadId: string | null,
): { parentType: ParentTypeOption; parentId: string | null } => {
  if (!isNullOrEmpty(activity.lead_id, true)) {
    return { parentType: 'Lead', parentId: activity.lead_id };
  }

  if (!isNullOrEmpty(activity.lot_id, true)) {
    return { parentType: 'Lot', parentId: activity.lot_id };
  }

  if (!isNullOrEmpty(activity.buyer_lead_id, true)) {
    return {
      parentType: 'BuyerLead',
      parentId: activity.buyer_lead_id,
    };
  }

  if (!isNullOrEmpty(activity.user_id, true)) {
    return { parentType: 'User', parentId: activity.user_id };
  }

  return { parentType: 'Lead', parentId: fallbackLeadId };
};

const extractTargetUserIds = (activity: ActivityDetails): string[] =>
  activity.activities_target_users
    .map(target => (target.target_user_id ?? '').trim())
    .filter(id => id !== '');

const toTimeString = (value?: string | null) =>
  value ? format(parseISO(value), 'HH:mm') : '';

const extractDateFromTimestamp = (timestamp?: string | null): string | null => {
  if (!timestamp) {
    return null;
  }

  // Extract YYYY-MM-DD from ISO timestamp
  return timestamp.slice(0, 10);
};

const buildBaseFormValues = (
  activity: ActivityDetails,
  lead: LeadDetailsDrawerData | null,
  userId: string | null,
) => {
  const targetIds = extractTargetUserIds(activity);
  const parent = resolveParentFromActivity(activity, lead?.id ?? null);

  return {
    assigned_to: activity.assigned_to ?? userId,
    contact_ids: targetIds,
    created_by: activity.created_by ?? userId,
    created_at: activity.created_at ?? null,
    parent_type: parent.parentType,
    parent_id: parent.parentId,
    visibility: activity.visibility ?? 'shared',
  };
};

export const mapCallActivityToFormValues = (
  activity: ActivityDetails,
  lead: LeadDetailsDrawerData | null,
  userId: string | null,
): LeadDetailsCallDialogFormValues => {
  const baseValues = buildBaseFormValues(activity, lead, userId);

  const status: LeadDetailsCallDialogFormValues['status'] = activity.done
    ? activity.success
      ? 'successful'
      : 'missed'
    : 'planned';

  // Fallback to extracting date from due_at if start_date is missing
  const date = activity.start_date ?? extractDateFromTimestamp(activity.due_at);

  return {
    ...baseValues,
    subject: activity.subject ?? '',
    status,
    date,
    time: toTimeString(activity.due_at),
    notes: activity.note ?? '',
  };
};

export const mapVisitActivityToFormValues = (
  activity: ActivityDetails,
  lead: LeadDetailsDrawerData | null,
  userId: string | null,
): LeadDetailsVisitDialogFormValues => {
  const baseValues = buildBaseFormValues(activity, lead, userId);
  const dueDate = activity.due_at ? parseISO(activity.due_at) : null;
  const endDate = activity.end_at ? parseISO(activity.end_at) : null;

  const duration =
    dueDate && endDate ? differenceInMinutes(endDate, dueDate) : undefined;

  const status: LeadDetailsVisitDialogFormValues['status'] = activity.done
    ? activity.success
      ? 'successful'
      : 'cancelled'
    : 'planned';

  // Fallback to extracting date from due_at if start_date is missing
  const date = activity.start_date ?? extractDateFromTimestamp(activity.due_at);

  return {
    ...baseValues,
    subject: activity.subject ?? '',
    status,
    date,
    time: toTimeString(activity.due_at),
    duration,
    location: activity.location ?? '',
    notes: activity.note ?? '',
  };
};

export const mapTaskActivityToFormValues = (
  activity: ActivityDetails,
  lead: LeadDetailsDrawerData | null,
  userId: string | null,
): LeadDetailsTaskDialogFormValues => {
  const baseValues = buildBaseFormValues(activity, lead, userId);

  const status: LeadDetailsTaskDialogFormValues['status'] = activity.done
    ? 'completed'
    : 'planned';

  // Fallback to extracting date from due_at if start_date is missing
  const date = activity.start_date ?? extractDateFromTimestamp(activity.due_at);

  return {
    ...baseValues,
    subject: activity.subject ?? '',
    status,
    date,
    time: toTimeString(activity.due_at),
    notes: activity.note ?? '',
  };
};

export const mapNoteActivityToFormValues = (
  activity: ActivityDetails,
  lead: LeadDetailsDrawerData | null,
  userId: string | null,
): LeadDetailsNoteDialogFormValues => {
  const baseValues = buildBaseFormValues(activity, lead, userId);

  return {
    ...baseValues,
    note: activity.note ?? '',
  };
};
